// Movement and Physics Module
class MovementManager {
    constructor(canvas) {
        // Canvas reference
        this.canvas = canvas;
        
        // Player position
        this.playerX = canvas.width / 2;
        this.playerY = canvas.height / 2;
        
        // Movement system properties
        this.velocity = { x: 0, y: 0 };
        this.keys = {};
        
        // Dynamic speed limits (initialized to default values)
        this.currentMaxSpeed = GameConfig.PHYSICS.MAX_SPEED;
        this.currentPreGameMaxSpeed = GameConfig.PHYSICS.PRE_GAME_MAX_SPEED;
    }
    
    handleKeyDown(e, game) {
        this.keys[e.key.toLowerCase()] = true;
        
        // Testing shortcut - Q to advance level
        if (e.key.toLowerCase() === 'q' && game.gameRunning) {
            const nextLevel = game.level + 1;
            if (GameConfig.LEVEL_CONFIGS[nextLevel]) {
                game.levelSkipped = true; // Mark that level was skipped
                
                // Calculate the cumulative score needed for the next level
                const currentLevelConfig = GameConfig.LEVEL_CONFIGS[game.level];
                const currentLevelThreshold = currentLevelConfig ? currentLevelConfig.pointsRequired : 0;
                const requiredScore = currentLevelThreshold + GameConfig.LEVEL_CONFIGS[nextLevel].pointsRequired;
                
                game.score = requiredScore; // Set to the total cumulative score needed
                game.checkLevelProgression();
            }
        }
        
        // M to toggle mute
        if (e.key.toLowerCase() === 'm') {
            game.audioManager.toggleMute();
        }
        
        // Space bar to use bomb
        if (e.key === ' ' && game.gameRunning && game.bombCount > 0) {
            game.useBomb();
        }
    }
    
    handleKeyUp(e) {
        this.keys[e.key.toLowerCase()] = false;
    }
    
    updateMovement(game) {
        const isPreGame = !game.gameRunning || (game.gameRunning && Date.now() - game.gameStartTime < 3000);
        const currentMaxSpeed = isPreGame ? 
            this.currentPreGameMaxSpeed : this.currentMaxSpeed;
        
        // Scale acceleration more aggressively to overcome friction at higher speeds
        const baseAcceleration = isPreGame ? 
            GameConfig.PHYSICS.PRE_GAME_ACCELERATION : GameConfig.PHYSICS.ACCELERATION;
        const friction = isPreGame ? 
            GameConfig.PHYSICS.PRE_GAME_FRICTION : GameConfig.PHYSICS.FRICTION;
        
        // Calculate acceleration needed to reach max speed considering friction
        // At equilibrium: acceleration = maxSpeed * (1 - friction)
        const currentAcceleration = currentMaxSpeed * (1 - friction) * 0.8;
        
        const currentFriction = isPreGame ? 
            GameConfig.PHYSICS.PRE_GAME_FRICTION : GameConfig.PHYSICS.FRICTION;

        // Apply acceleration based on keys pressed (check both arrow key names)
        if (this.keys['arrowup'] || this.keys['w']) this.velocity.y -= currentAcceleration;
        if (this.keys['arrowdown'] || this.keys['s']) this.velocity.y += currentAcceleration;
        if (this.keys['arrowleft'] || this.keys['a']) this.velocity.x -= currentAcceleration;
        if (this.keys['arrowright'] || this.keys['d']) this.velocity.x += currentAcceleration;

        // Apply friction
        this.velocity.x *= currentFriction;
        this.velocity.y *= currentFriction;

        // Limit speed
        const speed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y);
        if (speed > currentMaxSpeed) {
            this.velocity.x = (this.velocity.x / speed) * currentMaxSpeed;
            this.velocity.y = (this.velocity.y / speed) * currentMaxSpeed;
        }

        // Update position
        this.playerX += this.velocity.x;
        this.playerY += this.velocity.y;

        // Boundary checking
        const radius = game.characterSize / 2;
        if (this.playerX - radius < 0) {
            this.playerX = radius;
            this.velocity.x = 0;
        }
        if (this.playerX + radius > this.canvas.width) {
            this.playerX = this.canvas.width - radius;
            this.velocity.x = 0;
        }
        if (this.playerY - radius < 0) {
            this.playerY = radius;
            this.velocity.y = 0;
        }
        if (this.playerY + radius > this.canvas.height) {
            this.playerY = this.canvas.height - radius;
            this.velocity.y = 0;
        }
    }
    
    updateSpeedLimits(maxSpeed, preGameMaxSpeed) {
        this.currentMaxSpeed = maxSpeed;
        this.currentPreGameMaxSpeed = preGameMaxSpeed;
    }
    
    reset() {
        this.updatePlayerPosition();
        this.velocity = { x: 0, y: 0 };
        this.keys = {};
    }
    
    updatePlayerPosition() {
        // Center player on canvas
        this.playerX = this.canvas.width / 2;
        this.playerY = this.canvas.height / 2;
    }
}

// ES6 export for Vite
export default MovementManager;